<script setup>
import { computed, ref, onUnmounted, watch, onBeforeMount } from 'vue'
import { useRouter } from 'vue-router'
import { useTranslaterStore } from '@/stores/translater'
import { useLangStore } from '@/stores/lang'
import { useTrackStore } from '@/stores/track'
import { usePointsStore } from '@/stores/points'
import { useCarinfoStore } from '@/stores/car-info'
import { useCarAreasStore } from '@/stores/car-areas'
import { useCarEventsStore } from '@/stores/car-events'
import { useYMapCircle } from '@/composables/useYMapCircle.js'
import { useMakePopupContent } from '@/composables/useMakePopupContent.js'
import * as turf from '@turf/turf'
import vinDecoder from '@/helpers/vinDecoder'
import newElement from '@/helpers/newElement'
import colorToHex from '@/helpers/colorToHex.js'
import boundsFromCoords from '@/helpers/boundsFromCoords.js'

import Modal from '@/components/UTAModal.vue'
import UTACarTrack from '@/components/car/UTACarTrack.vue'
import UTACarMapEvents from '@/components/car/UTACarMapEvents.vue'
import UTACarMapList from './UTACarMapList.vue'

const { makePopupContent } = useMakePopupContent()

function changeStickMode() {
    stickOnCar.value = !stickOnCar.value
    if(stickOnCar.value) {
        if(
            lastCarLocation.value.coords.lat && 
            lastCarLocation.value.coords.lon
        ) {
            map.update({
                location: {
                    center: [lastCarLocation.value.coords.lon, lastCarLocation.value.coords.lat],
                    zoom: zoom.value, 
                    duration: 0
                }
            })
        }
    }
}

function resetTrack() {
    map.removeChild(markerStart)  // Удаляем маркер старта
    map.removeChild(markerFinish) // Удаляем маркер финиша
    map.removeChild(lineStringFeature.value)
    isTrackOnMap.value = false
    if(lastCarLocation.value.coords.lon && lastCarLocation.value.coords.lat) {
        map.update({
            location: {
                center: center.value,
                zoom: zoom.value, 
                duration: 0
            }
        })
    }

    if(clusterer.value) {
        map.removeChild(clusterer.value)
        clusterer.value = null
    }

    if(isShowEventsList.value) isShowEventsList.value = false
    if(track.value.length) trackStore.setTrack([])
    if(trackODM.value) trackStore.setTrackODM(null)
    if(eventsCarPart.value) carEventsStore.setEventsCarPart(null)
    carEventsStore.setEventsCarPartMsg({})
}

function showMarkerBaloon(event) {
    if(
        event.target.className == 'marker-finish' || 
        event.target.className == 'marker-start'
    ) {
        const markerBallon = event.target.firstChild
        if(markerBallon.classList.contains('map-balloon')) {
            markerBallon.classList.toggle('showed')
        }
    }

    if(event.target.classList.contains('marker-car__icon')) {
        const markerBallon = event.target.nextSibling
        if(markerBallon.classList.contains('map-balloon')) {
            markerBallon.classList.toggle('showed')
        }
    }    
}

function closeBaloon(event) {
    const parentClasses = event.target.parentElement.classList
    
    if(parentClasses.contains('showed')) {
        parentClasses.remove('showed')
    }
}

function mapClickHandler(event) {
    if(event?.target?.tagName == 'CANVAS') {
        markerStartBaloonElement.classList.remove('showed')
        markerFinishBaloonElement.classList.remove('showed')
    }
}

function mapEventsClickHandler(layer, coordinates) {
    if(layer?.type == 'feature') {

        // Зоны для отображения в popup
        intersectAreas.value = getIntersectAreas(coordinates.coordinates)

        if(intersectAreas.value?.length)
            createPopup(coordinates.coordinates, intersectAreas.value)
    }
}

function renderAllAreas(areas) {
    const { YMapFeature } = ymaps3

    areas.forEach(area => {
        // console.log('area: ', area)

        let layer = null
        const style = {
            stroke: [
                {
                    color: colorToHex(area.options?.strokeColor) || '#3388ff',
                    width: area.options?.strokeWidth || 0
                }
            ]
        }

        const areaDesc = {
            name: area.name,
            uuid: area.uuid,
            options: area.options
        }

        if(area.geometry["polygon"]) {
            const polygonCoords = area.geometry.polygon.array.map(latLng => [latLng.lon, latLng.lat])
            style.fill = colorToHex(area.options?.fillColor) || '#3388ff'
            
            layer = new YMapFeature({
                geometry: {
                    type: 'Polygon',
                    coordinates: [polygonCoords]
                },
                id: area.uuid,
                // draggable: true,
                // onClick: layerClickHandler,
                style: style
            })

            layer.area = areaDesc
            layer.area.type = "polygon"
        }

        if(area.geometry["line"]) {
            const lineCoords = area.geometry.line.array.map(latLng => [latLng.lon, latLng.lat])

            layer = new YMapFeature({
                geometry: {
                    type: 'LineString',
                    coordinates: lineCoords
                },
                id: area.uuid,
                // onClick: layerClickHandler,
                style: style
            })

            layer.area = areaDesc
            layer.area.type = "line"
        }

        if(area.geometry["circle"]) {
            const center = [area.geometry.circle.point.lon, area.geometry.circle.point.lat]
            const radius = area.geometry.circle.radius
            
            style.simplificationRate = 0
            style.fill = colorToHex(area.options?.fillColor) || '#3388ff'

            const options = {
                editable: false,
                uuid: area.uuid,
                style
            }

            const { yMapCircle } = useYMapCircle(center, radius, options)

            layer = yMapCircle.value
            layer.area = areaDesc
            layer.area.type = "circle"
        }
        // if(area.geometry["sector"]) {
        //     const center = [area.geometry.sector.circle.point.lon, area.geometry.sector.circle.point.lat]
        //     const radius = area.geometry.sector.circle.radius
        //     const bearing1 = area.geometry.sector.startBearing
        //     const bearing2 = area.geometry.sector.endBearing
            
        //     style.simplificationRate = 0
        //     style.fill = colorToHex(area.options?.fillColor) || '#3388ff'

        //     layer = new YMapFeature({
        //         geometry: getSectorGeoJSON(center, radius, bearing1, bearing2),
        //         id: area.uuid,
        //         // onClick: layerClickHandler,
        //         style: style
        //     })

        //     layer.area = areaDesc
        //     layer.area.type = "sector"
        // }
        
        if(layer) map.addChild(layer)
    })
}

// Получение пересекающих зон в точке с координатами [coordinates]
function getIntersectAreas(coordinates) {
    const intersectAreas = []
    
    areas.value?.forEach(area => {
        const point = turf.point(coordinates)

        if(area.geometry['line']) {
            const coords = area.geometry.line.array.map(latLng => [latLng.lon, latLng.lat])
            
            const line = turf.lineString(coords)
            
            const contains = turf.booleanIntersects(line, point) 
            
            if(contains) intersectAreas.push(area)

            // Точность определения линии (расстояние от клика до линии)
            const precision = turf.nearestPointOnLine(line, point, {units: 'meters'}).properties.dist

            if(precision < +area.options?.strokeWidth * 1.2) {
                area.type = 'line'
                intersectAreas.push(area)
            }
        }

        if(area.geometry['polygon']) {
            const coords = area.geometry.polygon.array.map(latLng => [latLng.lon, latLng.lat])
            const polygon = turf.polygon([coords])

            // const contains = turf.booleanPointInPolygon(point, polygon)
            const contains = turf.booleanIntersects(point, polygon)

            if(contains) {
                area.type = 'polygon'
                intersectAreas.push(area)
            }
        }

        if(area.geometry['circle']){
            const center = [area.geometry.circle.point.lon, area.geometry.circle.point.lat]
            const radiusMeters = area.geometry.circle.radius
            const circle = turf.circle(center, radiusMeters, {units: 'meters'})

            const contains = turf.booleanIntersects(point, circle)
            
            if(contains) {
                area.type = 'circle'
                intersectAreas.push(area)
            }
        }

        // if(area.geometry['sector']){
        //     const center = [area.geometry.sector.circle.point.lon, area.geometry.sector.circle.point.lat]
        //     const radiusMeters = area.geometry.sector.circle.radius
        //     const bearing1 = area.geometry.sector.startBearing
        //     const bearing2 = area.geometry.sector.endBearing

        //     const sector = turf.sector(center, radiusMeters, bearing1, bearing2, {units: 'meters'})

        //     const contains = turf.booleanIntersects(point, sector)
            
        //     if(contains) {
        //         area.type = 'sector'
        //         intersectAreas.push(area)
        //     }
        // }
    })

    return intersectAreas
}

// Создание popup
async function createPopup(coordinates, areas) {
    if(popup.value) closePopup()

    // Import the package to add a default marker
    const {YMapDefaultMarker} = await ymaps3.import('@yandex/ymaps3-default-ui-theme')   

    popup.value = new YMapDefaultMarker({
        iconName: 'fallback',
        size: 'micro', // noraml, small, micro
        coordinates: coordinates,
        popup: {
            content: () => makePopupContent(coordinates, areas),
            position: 'top',
        }
    })

    // Добавить на карте
    map.addChild(popup.value)

    // Показать попап
    popup.value.update({
        popup: {
            show: true
        }
    })
}

// Очистка Handler клика ссылок в Popup зон
function clearPopupLinks () {
    if (popupLinks.value) {
        for (let link of popupLinks.value) {
            link.removeEventListener('click', popupLinkHandler)                        
        }
    }          

    popupLinks.value = null
}

// Handler клика ссылок в Popup зон (Для реактивного перехода из ссылок в Popup)
function popupLinkHandler(e) {
    e.preventDefault()

    // Очистка ссылок из Popup
    clearPopupLinks()

    router.push(e.target.pathname)
}

// Закрытие popup
function closePopup() {
    map.removeChild(popup.value)
    
    popup.value = null
    // intersectAreas.value = []
}

// Handler кнопки закрытия Popup
function popupCloseHandler(e) {
    e.stopPropagation()

    // Закрыть Popup
    closePopup()

    popupCloseBtn.value.removeEventListener('click', popupCloseHandler)
    popupCloseBtn.value = null
}

// Клики в Popup (для реактивности)
function updatePopupClickHadlers() {
    popupLinks.value = document.getElementsByClassName('uta-areas-popup__areas-item_link')

    for (let link of popupLinks.value) {
        link.addEventListener('click', popupLinkHandler)
    }

    popupCloseBtn.value = document.getElementsByClassName('uta-areas-popup__close')[0]
    popupCloseBtn.value.addEventListener('click', popupCloseHandler)
}

// Контент для Popup события
function makePopupEvent(event) {
    const popupElement = document.createElement('div')
    popupElement.classList.add('uta-car-map-popup')

    const popupElementText = document.createElement('div')
    popupElementText.classList.add('uta-car-map-popup__text')

    const popupElementLat = document.createElement('div')
    popupElementLat.textContent = `Дата: ${new Date(event.createdAt / 1000).toLocaleString()}`
    popupElementText.appendChild(popupElementLat)
    
    const popupElementLng = document.createElement('div')
    popupElementLng.textContent = `Событие: ${getTranslate.value['EVENT_' + event.eventType.toUpperCase()]}`
    popupElementText.appendChild(popupElementLng)

    const closeIconElement = document.createElement('button')
    closeIconElement.classList.add('uta-car-map-popup__close', 'uta-car-map-close_icon')

    popupElement.appendChild(popupElementText)
    popupElement.appendChild(closeIconElement)

    return popupElement
}

// Handler закрытия Popup события
function currentMarkerCloseHandler() {
    currentMarkerCloseBtn.value.removeEventListener('click', currentMarkerCloseHandler)
    currentMarkerCloseBtn.value = null
    
    currentMarker.value.update({
        size: 'normal',
        popup: {
            show: false,
            content: () => document.createElement('div')
        }
    })
    
    currentMarkerContent.value = null
    currentMarker.value = null
}

async function buildEvents(carEvents) {
    // console.log('buildEvents: ', carEvents)

    if(!carEvents?.array?.length) return

    if(clusterer.value) {
        map.removeChild(clusterer.value)
        clusterer.value = null
    }

    const {YMapMarker} = ymaps3
    const {YMapDefaultMarker} = await ymaps3.import('@yandex/ymaps3-default-ui-theme')
    const {YMapClusterer, clusterByGrid} = await ymaps3.import('@yandex/ymaps3-clusterer@0.0.1')

    const events = carEvents.array.filter(event => {
        if(event.coords) {
            return eventsMarkers.value.includes(event.type) || eventsMarkers.value.includes('all')
        } 

        return false
    })
    
    const points = events.map(event => {
        return {
            type: 'Feature',
            id: event.createdAt,
            geometry: { type: 'Point', coordinates: [event.coords.lon, event.coords.lat] },
            eventType: event.type,
            createdAt: event.createdAt
        }
    })

    const iconNames = {
        // Двигатель включение-выключение
        'engine_off': 'ya_electro_station', // auto_repair, car, auto_parts
        'engine_on': 'ya_electro_station',
        'remote_engine_on': 'ya_electro_station', 
        'cmd_engine_on': 'ya_electro_station', 
        'cmd_engine_off': 'ya_electro_station',

        // Охрана
        'locked': 'locker', 
        'unlocked': 'locker', 
        'cmd_lock': 'locker', 
        'cmd_unlock': 'locker',

        // Багажник
        'cmd_trunk_open': 'auto',
        'cmd_trunk_close': 'auto',

        // Геозона
        'protected_area_in': 'indoor_info_stand', 
        'protected_area': 'indoor_info_stand',

        // Освещение и сигнал
        'cmd_light': 'car', 
        'cmd_beep': 'car',

        // Уменьшение пробега
        'decrease_mileage': 'information',

        // Низкий заряд АКБ  
        'low_battery_voltage': 'indoor_service_charging_socket', // indoor_service_charging_socket

        // Срабатывание штатной сигнализации
        'alarm_system_triggered': 'information'
    }

    const marker = (feature) => {
        const mrkr = new YMapDefaultMarker({
            coordinates: feature.geometry.coordinates,
            iconName: iconNames[feature.eventType] || 'fallback',
            size: 'normal', // normal , small , micro 
            color: 'blue', // darkblue , red, lavender, tulip, orange, green, lightgreen, blue
            onClick() {
                if(currentMarker.value) {
                    currentMarkerCloseBtn.value.removeEventListener('click', currentMarkerCloseHandler)
                    currentMarkerCloseBtn.value = null

                    currentMarker.value.update({
                        size: 'normal',
                        popup: {
                            show: false,
                            content: () => document.createElement('div')
                        }
                    })                    

                    currentMarkerContent.value = null
                    currentMarker.value = null
                }

                currentMarkerContent.value = feature
                currentMarker.value = mrkr

                currentMarker.value.update({
                    size: 'small',
                    popup: {
                        show: true,
                        content: () => makePopupEvent(currentMarkerContent.value),
                        position: 'top',
                    }
                })
                
                currentMarkerCloseBtn.value = document.getElementsByClassName('uta-car-map-popup__close')[0]
                currentMarkerCloseBtn.value.addEventListener('click', currentMarkerCloseHandler)
            }
        })

        return mrkr
    }

    const cluster = (coordinates, features) => new YMapMarker(
        {
            coordinates,
            onClick() {
                const carEvents = {
                    list: features,
                    dateStart: track.value[0].ntm,
                    dateStop: track.value[track.value.length - 1].ntm
                }

                eventsList.value.show(carEvents)
                isShowEventsList.value = true
            }
        },
        circle(features.length).cloneNode(true)
    )
    
    function circle(count) {
        const circle = document.createElement('div')
        circle.classList.add('cluster')
        circle.innerHTML = `
            <div class="cluster-content">
                <span class="cluster-text">${count}</span>
            </div>
        `
        return circle
    }

    clusterer.value = new YMapClusterer({
        method: clusterByGrid({gridSize: 64}),
        features: points,
        marker,
        cluster
    })

    map.addChild(clusterer.value)
}

// Обновление после фильтрации
function setEventsMarkers(data) {
    // console.log(data)

    eventsMarkers.value = []

    data.forEach((item) => {

        switch(item) {

            case 'UTACarMapEvent_secure': eventsMarkers.value.push('locked', 'unlocked', 'cmd_lock', 'cmd_unlock', 'alarm_system_triggered')
                break

            case 'UTACarMapEvent_engine': eventsMarkers.value.push('engine_on', 'engine_off', 'remote_engine_on', 'cmd_engine_on', 'cmd_engine_off')
                break

            case 'UTACarMapEvent_trunk': eventsMarkers.value.push('cmd_trunk_open', 'cmd_trunk_close')
                break

            case 'UTACarMapEvent_zone': eventsMarkers.value.push('protected_area_in', 'protected_area')
                break

            case 'UTACarMapEvent_light': eventsMarkers.value.push('cmd_light', 'cmd_beep')
                break

            case 'UTACarMapEvent_mileage': eventsMarkers.value.push('decrease_mileage')
                break

            case 'UTACarMapEvent_voltage': eventsMarkers.value.push('low_battery_voltage')
                break

            // case 'UTACarMapEvent_alarm': eventsMarkers.value.push('alarm_system_triggered')
            //     break
        }
    })

    buildEvents(eventsCarPart.value)

    eventsList.value.close()
    isShowEventsList.value = false
}

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const router            = useRouter()

const trackStore        = useTrackStore()
const translaterStore   = useTranslaterStore()
const langStore         = useLangStore()
const pointsStore       = usePointsStore()
const carinfoStore      = useCarinfoStore()
const carAreasStore     = useCarAreasStore()
const carEventsStore    = useCarEventsStore()

const getTranslate  = computed(() => translaterStore.getTranslate)
const lang          = computed(() => langStore.lang)
const track         = computed(() => trackStore.track)
const trackODM      = computed(() => trackStore.trackODM)
const point         = computed(() => pointsStore.point)
const carinfo       = computed(() => carinfoStore.carinfo)
const areas         = computed(() => carAreasStore.carAreas.areas)
const eventsCarPart = computed(() => carEventsStore.eventsCarPart)

const vinDecode = vinDecoder(carinfo.value.data.vin)
const modelName = vinDecode.name

const mapElement        = ref(null)
const clusterer         = ref(null)
const eventsList        = ref(null)
const currentMarker     = ref(null)
const currentMarkerContent = ref(null)
const currentMarkerCloseBtn = ref(null)
const showRequestTrack  = ref(false)
const fullscreenMode    = ref(false)
const lineStringFeature = ref({})
const isTrackOnMap      = ref(false)
const stickOnCar        = ref(true)
const showMapCrachMsg   = ref(false)
const isShowEventsList  = ref(false)
const center            = ref([48.332417, 54.310017])
const zoom              = ref(18)
const lastCarLocation   = ref({
    coords: {},
    dir: 0
})
const eventsMarkers     = ref(['all'])
const intersectAreas    = ref([])
const popup             = ref(null)
const popupCloseBtn     = ref(null)
const popupLinks        = ref(null)

const centerLocal   = localStorage.getItem('UTA_CAR_MAP_CENTER') 
const zoomLocal     = localStorage.getItem('UTA_CAR_MAP_ZOOM')

if(centerLocal) {
    center.value = JSON.parse(centerLocal)
}

if(zoomLocal) {
    zoom.value = JSON.parse(zoomLocal)
}

const { 

    YMap,                       // Корневой класс карты
    YMapDefaultSchemeLayer,     // Объект для отображения данных [тайлы]
    YMapDefaultFeaturesLayer,   // Слой для объектов на карте
    YMapFeature,                // Слой геообъектов [линия трека, маркеры]
    YMapMarker,                 // Слой с DOM-элементами для YMapFeature
    YMapListener                // Класс для подписки на события карты (клик, двиг и т.д.)

// eslint-disable-next-line no-undef
} = ymaps3

pointsStore.readLatestPoint({ uuid_car_array: props.uuid })

// Маркер трека СТАРТ [начало]
const markerStartElement = newElement('div', 'marker-start')

    const markerStartBaloonElement      = newElement('div',  ['map-balloon', 'marker-car__balloon'])
    const markerStartBaloonTextElement  = newElement('span', 'map-balloon__text')
    const markerStartBaloonCloseElement = newElement('span', ['map-balloon__close', 'material-icons'], 'close')

    markerStartBaloonElement.appendChild(markerStartBaloonTextElement)
    markerStartBaloonElement.appendChild(markerStartBaloonCloseElement)
    markerStartElement.appendChild(markerStartBaloonElement)

    markerStartElement.addEventListener('click', showMarkerBaloon)
    markerStartBaloonCloseElement.addEventListener('click', closeBaloon)

const markerStart = new YMapMarker({ zIndex: 10 }, markerStartElement)
// Маркер трека СТАРТ [конец]

// Маркер трека ФИНИШ [начало]
const markerFinishElement = newElement('div', 'marker-finish')

    const markerFinishBaloonElement         = newElement('div',  ['map-balloon', 'marker-car__balloon'])
    const markerFinishBaloonTextElement     = newElement('span', 'map-balloon__text')
    const markerFinishBaloonCloseElement    = newElement('div', ['map-balloon__close', 'material-icons'], 'close')

    markerFinishBaloonElement.appendChild(markerFinishBaloonTextElement)
    markerFinishBaloonElement.appendChild(markerFinishBaloonCloseElement)
    markerFinishElement.appendChild(markerFinishBaloonElement)

    markerFinishElement.addEventListener('click', showMarkerBaloon)
    markerFinishBaloonCloseElement.addEventListener('click', closeBaloon)

const markerFinish = new YMapMarker({ zIndex: 10 }, markerFinishElement)
// Маркер трека ФИНИШ [конец]

// Маркер автомобиля [начало]
const markerCarElement = newElement('div', 'marker-car')

    const markerCarIconElement          = newElement('div', ['marker-car__icon', `marker-car__icon--${modelName}`])
    const markerCarBaloonElement        = newElement('div', ['map-balloon', 'marker-car__balloon'])
    const markerCarBaloonTextElement    = newElement('span', ['map-balloon__text', 'marker-car__balloon-text'])
    const markerCarBaloonCloseElement   = newElement('div', ['map-balloon__close', 'material-icons'], 'close')
    const markerCarBaloonSpeedElement   = newElement('div', 'marker-car__balloon-text-item')
    const markerCarBaloonDateElement    = newElement('div', 'marker-car__balloon-text-item')

    markerCarBaloonElement.appendChild(markerCarBaloonTextElement)
    markerCarBaloonElement.appendChild(markerCarBaloonCloseElement)
    markerCarElement.appendChild(markerCarIconElement)
    markerCarElement.appendChild(markerCarBaloonElement)

    markerCarBaloonSpeedElement.innerHTML = `<strong>${getTranslate.value.CARMAP_SPEED}:</strong><br />`
    markerCarBaloonSpeedElement.append('-')

    markerCarBaloonDateElement.innerHTML = `<strong>${getTranslate.value.CARMAP_UPDATE_DATE}:</strong><br />`
    markerCarBaloonDateElement.append('-')

    markerCarBaloonTextElement.append(markerCarBaloonSpeedElement)
    markerCarBaloonTextElement.append(markerCarBaloonDateElement)

    markerCarIconElement.addEventListener('click', showMarkerBaloon)
    markerCarBaloonCloseElement.addEventListener('click', closeBaloon)

    // 30 53
    const markerCar = new YMapMarker({ 
        zIndex: 3 
    }, markerCarElement)

// Маркер автомобиля [конец]

const updateMapInfo = data => {

    zoom.value = data.location?.zoom || 18
    center.value = data.location.center || [48.332417, 54.310017]

    localStorage.setItem('UTA_CAR_MAP_ZOOM', zoom.value)
    localStorage.setItem('UTA_CAR_MAP_CENTER', JSON.stringify(center.value))
}

const mapListener = new YMapListener({

    layer: 'any',
    onActionEnd: updateMapInfo,
    onClick: mapClickHandler
})

const mapEvents = new YMapListener({
    onClick: mapEventsClickHandler
})

let map = {}

try {

    // eslint-disable-next-line no-undef
    ymaps3.ready.then(() => {

        mapElement.value = document.getElementById('UTAMap')

        // eslint-disable-next-line no-undef
        map = new YMap(
            mapElement.value, 
            {
                location: {
                    center: center.value,
                    zoom: zoom.value, 
                }
            }
        )

        map.addChild(new YMapDefaultSchemeLayer({}))
        map.addChild(new YMapDefaultFeaturesLayer({}))
        map.addChild(mapListener)
        map.addChild(mapEvents)

        mapElement.value.addEventListener('click', mapClickHandler)
    })

} catch(error) {

    showMapCrachMsg.value = true
}

onBeforeMount(async() => {

    const isUseStore = true
    const params = {
        'uuid_car_array': props.uuid,
        'c.limit': 1000
    }
        
    await carAreasStore.readCarAreas(params, isUseStore)

    if(areas.value?.length) renderAllAreas(areas.value)   
})

onUnmounted(() => {

    if(map) {

        map.destroy()
        mapElement.value.removeEventListener('click', mapClickHandler)
    }

    if(track.value.length) trackStore.setTrack([])
    if(trackODM.value) trackStore.setTrackODM(null)
    if(eventsCarPart.value) carEventsStore.setEventsCarPart(null)
    carEventsStore.setEventsCarPartMsg({})
})

watch(track, trackPoints => {

    if(isTrackOnMap.value) {
        resetTrack()
    }

    if(trackPoints.length) {

        const coords = []

        let startTm = trackPoints[0].ntm, 
            finishTm = trackPoints[trackPoints.length - 1].ntm

        trackPoints.map(point => coords.push([point.coords.lon, point.coords.lat]))

        lineStringFeature.value = new YMapFeature(
            {
                id: 'line',
                zIndex: 1,
                geometry: {
                    type: 'LineString',
                    coordinates: coords
                },
                style: {
                    stroke: [{ width: 3, color: '#FF0000' }]
                }
            }
        )

        markerStartBaloonTextElement.innerText = getTranslate.value.CARMAP_TRACK_START + ': ' + new Date(Number(startTm * 1000)).toLocaleString()
        markerFinishBaloonTextElement.innerText = getTranslate.value.CARMAP_TRACK_FINISH + ': ' + new Date(Number(finishTm * 1000)).toLocaleString()

        markerStart.update({ coordinates: coords[0] })
        markerFinish.update({ coordinates: coords[coords.length - 1]})

        map.addChild(lineStringFeature.value)
        map.addChild(markerStart)
        map.addChild(markerFinish)

        map.update({
            location: {
                bounds: boundsFromCoords(coords),
                duration: 2000
            },
            margin: [100, 100, 100, 100]
        })

        isTrackOnMap.value = true
    }
})

watch(point, point => {

    if(point.vld) {

        lastCarLocation.value = {
            coords: point.coords, 
            dir: point.dir
        }

        markerCarIconElement.setAttribute("style", `transform: rotate(${point.dir}deg)`)

        markerCar.update({
            coordinates: [point.coords.lon, point.coords.lat]
        })

        markerCarBaloonSpeedElement.innerHTML = `<strong>${getTranslate.value.CARMAP_SPEED}:</strong><br />`
        markerCarBaloonSpeedElement.append(Number(point.spd).toFixed() + ' ' + getTranslate.value.UNIT_KMH || '-')

        markerCarBaloonDateElement.innerHTML = `<strong>${getTranslate.value.CARMAP_UPDATE_DATE}:</strong><br />`
        markerCarBaloonDateElement.append(point.ntm ? new Date(Number(point.ntm * 1000)).toLocaleString() : '-')

        map.addChild(markerCar)

        if(!isTrackOnMap.value && stickOnCar.value) {
            map.update({
                location: {
                    center: [point.coords.lon, point.coords.lat],
                    zoom: Number(zoom.value), 
                    duration: 0
                }
            })
        }
    }
})

watch(
    eventsCarPart, 
    (eventsCar) => {

        // Добавить на карту маркеры событий
        if(eventsCar) buildEvents(eventsCar)
    },
    { deep: true}
)

// Перехват кликов в Popup (для реактивности)
watch(popup, (newVal) => {
    if(newVal) updatePopupClickHadlers()
})

// Обновить Popup при смене языка
watch(lang, () => {
    if(popup.value) {
        const coordinates = popup.value._popup.coordinates
        
        popup.value.update({
            popup: {
                content: () => makePopupContent(coordinates, intersectAreas.value),
            }
        })

        // Обновить клики в Popup
        updatePopupClickHadlers()
    }

    // Обновить открытый Popup события
    if(currentMarker.value && currentMarkerContent.value) {
        currentMarkerCloseBtn.value.removeEventListener('click', currentMarkerCloseHandler)

        currentMarker.value.update({
            size: 'small',
            popup: {
                content: () => makePopupEvent(currentMarkerContent.value),
            }
        })

        currentMarkerCloseBtn.value = document.getElementsByClassName('uta-car-map-popup__close')[0]
        currentMarkerCloseBtn.value.addEventListener('click', currentMarkerCloseHandler)
    }
})
</script>

<template>
    <div class="uta-car-map" :class="{ 'uta-car-map--fullscreen' : fullscreenMode }">
        <div class="uta-car-map__container" id="UTAMap"></div>
        <div class="uta-car-map__controls uta-car-map__controls--rt uta-car-map__controls--vertical-mobile ">
            <div 
                class="uta-car-map__controls-btn" 
                :class="{ 'uta-car-map__controls-btn--active' : stickOnCar }"
                @click="changeStickMode()"
            >
                <span class="material-symbols-outlined">
                    near_me
                </span>
            </div>
            <div class="uta-car-map__controls-btn" @click="showRequestTrack = true">
                <span class="material-symbols-outlined">
                    route
                </span>
            </div>
            <div class="uta-car-map__controls-btn" @click="resetTrack" v-if="isTrackOnMap">
                <span class="material-icons">
                   close
                </span>
            </div>
            <div
                v-if="!fullscreenMode" 
                class="uta-car-map__controls-btn order-first order-sm-last" 
                @click="fullscreenMode = true"
            >
                <span class="material-symbols-outlined">
                    fullscreen
                </span>
            </div>
            <div
                v-if="fullscreenMode" 
                class="uta-car-map__controls-btn uta-car-map__controls-btn--active order-first order-sm-last" 
                @click="fullscreenMode = false"
            >
                <span class="material-symbols-outlined">
                    fullscreen_exit
                </span>
            </div>
        </div>
        <div class="uta-car-map__controls uta-car-map__controls--rb"></div>
        <div v-if="trackODM" class="uta-car-map__controls uta-car-map__controls--lb">
            <div class="uta-car-map__mileage">
                {{ getTranslate.CAR_TRIPS_MILEAGE }}: {{ trackODM }} {{ getTranslate.UNIT_KM }}
            </div>
        </div>
        <div v-if="eventsCarPart" class="uta-car-map__controls uta-car-map__controls--lm">
            <UTACarMapList ref="eventsList" @close="isShowEventsList = false" />
        </div>
        <div v-if="track.length" class="uta-car-map__controls uta-car-map__controls--lt">
            <UTACarMapEvents @setEventsMarkers="setEventsMarkers" :is-right="isShowEventsList" />
        </div>
        <transition name="bounce">
            <Modal 
                v-if="showRequestTrack"
                :header="getTranslate.CARMAP_TRACK_TITLE"
                size="md"
                @closeModal="showRequestTrack = false"
            >
                <template v-slot:body>
                    <UTACarTrack :uuid="props.uuid" @closeModal="showRequestTrack = false" />
                </template>
            </Modal>
        </transition>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-map';
@import '@/assets/styles/scss/_balloon';
@import '@/assets/styles/scss/components/areas/areas-popup.scss';
</style>
